import type { Permission, Role } from '@natashu/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { FullPageError, SplashScreen } from '../components/SplashScreen.tsx';
import { api, ApiError, configureApiAuth, setCsrfToken } from './api.ts';
import { useLatest } from './hooks.ts';
import { satisfies, type PermissionRequirement } from './permissions.ts';

/** GET /api/auth/me payload (apps/server/src/modules/auth/routes.ts → mePayload). */
export interface Me {
  user: {
    id: string;
    email: string;
    displayName: string;
    role: Role;
    mfaEnabled: boolean;
    mustChangePassword: boolean;
  };
  workspace: { id: string; name: string; timezone: string; folderMaxDepth: number };
  permissions: Permission[];
  csrfToken: string;
  mfaPending: boolean;
}

export type AuthStatus = 'loading' | 'error' | 'anonymous' | 'mfa_pending' | 'authenticated';

export const ME_QUERY_KEY = ['me'] as const;

export interface AuthContextValue {
  status: AuthStatus;
  me: Me | null;
  error: unknown;
  permissions: ReadonlySet<Permission>;
  /** True when the user holds the permission (or any of a list). UI hint only — the API enforces. */
  can: (requirement: PermissionRequirement) => boolean;
  login: (email: string, password: string) => Promise<{ mfaRequired: boolean }>;
  verifyMfa: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<Me | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchMe(signal?: AbortSignal): Promise<Me | null> {
  try {
    const me = await api.get<Me>('/api/auth/me', { skipAuthRedirect: true, signal });
    setCsrfToken(me.csrfToken);
    return me;
  } catch (err) {
    if (err instanceof ApiError && err.code === 'AUTH_REQUIRED') {
      setCsrfToken(null);
      return null;
    }
    throw err;
  }
}

/** "/login?next=%2Finbox" — keeps the path the user was trying to open. */
export function withNext(target: string, location: { pathname: string; search: string }): string {
  const current = location.pathname + location.search;
  if (current === '/' || current.startsWith('/login')) return target;
  return `${target}?next=${encodeURIComponent(current)}`;
}

/** Only same-app relative paths are accepted as redirect targets. */
export function safeNext(next: string | null | undefined, fallback = '/'): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/login')) return fallback;
  return next;
}

/**
 * Data that belongs to the signed-in user and must be dropped when nobody is signed in. Keys that start with
 * "public-" are the pages anyone may open (the public team list, the legal pages) and stay (v1.42.0: the public
 * page kept reloading for ever because its query was removed the moment the session came back as anonymous).
 */
const isSessionScoped = (key: readonly unknown[]) => key[0] !== ME_QUERY_KEY[0] && !(typeof key[0] === 'string' && key[0].startsWith('public'));

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useLatest(location);

  const meQuery = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 60_000,
    retry: (count, err) => err instanceof ApiError && err.retryable && count < 2,
  });

  const me = meQuery.data ?? null;
  const status: AuthStatus =
    meQuery.data === undefined ? (meQuery.isError ? 'error' : 'loading') : !me ? 'anonymous' : me.mfaPending ? 'mfa_pending' : 'authenticated';

  const refresh = useCallback(
    () => queryClient.fetchQuery({ queryKey: ME_QUERY_KEY, queryFn: ({ signal }) => fetchMe(signal), staleTime: 0 }),
    [queryClient],
  );

  // Drop cached data from another session as soon as we know nobody is signed in.
  useEffect(() => {
    if (status === 'anonymous') queryClient.removeQueries({ predicate: (q) => isSessionScoped(q.queryKey) });
  }, [status, queryClient]);

  useEffect(
    () =>
      configureApiAuth({
        onAuthRequired: () => {
          setCsrfToken(null);
          queryClient.setQueryData(ME_QUERY_KEY, null);
          const loc = locationRef.current;
          if (!loc.pathname.startsWith('/login')) navigate(withNext('/login', loc), { replace: true });
        },
        onMfaRequired: () => {
          void refresh().catch(() => undefined);
          const loc = locationRef.current;
          if (loc.pathname !== '/login/mfa') navigate(withNext('/login/mfa', loc), { replace: true });
        },
        onPasswordChangeRequired: () => {
          void refresh().catch(() => undefined);
          if (locationRef.current.pathname !== '/account/password') navigate('/account/password', { replace: true });
        },
        refreshSession: async () => (await refresh())?.csrfToken ?? null,
      }),
    [navigate, queryClient, refresh, locationRef],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.post<{ mfaRequired: boolean; csrfToken: string }>('/api/auth/login', { email, password }, { skipAuthRedirect: true });
      setCsrfToken(res.csrfToken);
      queryClient.removeQueries({ predicate: (q) => isSessionScoped(q.queryKey) });
      await refresh();
      return { mfaRequired: res.mfaRequired };
    },
    [queryClient, refresh],
  );

  const verifyMfa = useCallback(
    async (code: string) => {
      await api.post('/api/auth/mfa/verify', { code }, { skipAuthRedirect: true });
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout', undefined, { skipAuthRedirect: true });
    } catch {
      // The session may already be gone; sign out locally regardless.
    }
    setCsrfToken(null);
    queryClient.clear();
    queryClient.setQueryData(ME_QUERY_KEY, null);
    navigate('/login', { replace: true });
  }, [navigate, queryClient]);

  const permissions = useMemo(() => new Set<Permission>(me?.permissions ?? []), [me]);
  const can = useCallback((req: PermissionRequirement) => satisfies(permissions, req), [permissions]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, error: meQuery.error, permissions, can, login, verifyMfa, logout, refresh }),
    [status, me, meQuery.error, permissions, can, login, verifyMfa, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Route guard: splash while loading, login/MFA redirects, forced password change. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, me, error, refresh } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <SplashScreen />;
  if (status === 'error') return <FullPageError error={error} onRetry={() => void refresh().catch(() => undefined)} />;
  if (status === 'anonymous') return <Navigate to={withNext('/login', location)} replace />;
  if (status === 'mfa_pending') return <Navigate to={withNext('/login/mfa', location)} replace />;
  if (me?.user.mustChangePassword && location.pathname !== '/account/password') return <Navigate to="/account/password" replace />;
  return <>{children}</>;
}

/** Renders children only when the user holds the permission (or any of a list). */
export function Can({ permission, children, fallback = null }: { permission: PermissionRequirement; children: ReactNode; fallback?: ReactNode }) {
  const { can } = useAuth();
  return <>{can(permission) ? children : fallback}</>;
}
